import type { z } from 'zod';
import { ProblemDetails } from './types';

/**
 * A thin fetch wrapper. Every response is validated against a Zod schema before
 * the rest of the app sees it — a shape the server never actually sends should
 * fail loudly here, not surface as a confusing crash three components away.
 *
 * Auth is a session cookie the browser attaches automatically
 * (`credentials: 'include'`) — there is no token to acquire or attach here
 * any more. The CSRF token is the one piece of session state the client
 * still holds, and only ever in memory (never persisted): AuthProvider sets
 * it from GET /auth/session's response, and it dies with the tab.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, fallbackMessage: string) {
    super(problem?.detail ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Distinguishes "not signed in" from any other failure (e.g. the API being down). */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Exported for the two places that navigate the whole page rather than
 * `fetch` (AuthProvider's sign-in/-out redirects). `fetch` calls in this file
 * already resolve correctly against a cross-origin API because the full URL
 * is built with this constant every time — but `window.location.assign`
 * calls that hardcoded a bare `/api/v1/...` path resolved against the SPA's
 * OWN origin instead, which is only harmless when the two happen to be the
 * same origin (e.g. the local dev proxy). On a two-subdomain deployment
 * (mci.ethub.co / mciapi.ethub.co) that 404s on the SPA's own nginx instead of
 * ever reaching the API. Always build absolute-origin navigations from this,
 * never from a bare path.
 */
export const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const SAFE_METHODS = new Set(['GET', 'HEAD', undefined]);

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  if (!SAFE_METHODS.has(init.method) && csrfToken) headers.set('X-CSRF-Token', csrfToken);

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });

  if (!res.ok) {
    let problem: ProblemDetails | null = null;
    try {
      const body: unknown = await res.json();
      const parsed = ProblemDetails.safeParse(body);
      if (parsed.success) problem = parsed.data;
    } catch {
      // Non-JSON error body — problem stays null and the fallback message is used.
    }
    throw new ApiError(res.status, problem, `Request to ${path} failed with ${String(res.status)}`);
  }

  if (res.status === 204) return schema.parse(undefined);

  const body: unknown = await res.json();
  return schema.parse(body);
}

export const api = {
  get: <T>(path: string, schema: z.ZodType<T>): Promise<T> => request(path, schema),
  post: <T>(path: string, schema: z.ZodType<T>, body?: unknown): Promise<T> =>
    request(path, schema, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  patch: <T>(path: string, schema: z.ZodType<T>, body?: unknown): Promise<T> =>
    request(path, schema, { method: 'PATCH', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  delete: <T>(path: string, schema: z.ZodType<T>): Promise<T> => request(path, schema, { method: 'DELETE' }),
};
